import type { TextMatchTransformer } from '@lexical/markdown';
import type { TextNode } from 'lexical';
import { $createMentionNode, $isMentionNode, MentionNode } from './MentionNode';
import type { MentionSource } from './types';

/**
 * Matches {key:id} tokens in markdown text.
 * e.g. {tool:login_remote_desktop}, {user:abc-123}
 */
const MENTION_IMPORT_REGEX = /\{(\w+):([^\s}]+)\}/;

/**
 * Creates a TextMatchTransformer that converts {key:id} tokens to/from MentionNodes.
 * This is a factory so the transformer has access to the sources array for display name resolution.
 */
export function createMentionTransformer(
  sources: MentionSource[],
): TextMatchTransformer {
  return {
    dependencies: [MentionNode],
    // For export: MentionNode → {key:id}
    export(node) {
      if (!$isMentionNode(node)) {
        return null;
      }
      return `{${node.__mentionKey}:${node.__mentionId}}`;
    },
    // importRegExp is used when loading markdown from string
    importRegExp: MENTION_IMPORT_REGEX,
    // regExp is used for live-typing shortcuts — we don't want to trigger on typing
    // so we use a pattern that can't be typed (requires braces + colon + no space)
    regExp: MENTION_IMPORT_REGEX,
    // replace is called when the import regex matches during $convertFromMarkdownString
    replace(textNode: TextNode, match: RegExpMatchArray) {
      const [, mentionKey, mentionId] = match;
      if (!mentionKey || !mentionId) return;

      const source = sources.find((s) => s.key === mentionKey);
      const displayName = source?.resolveDisplayName(mentionId) ?? mentionId;
      // Suppress unused variable warning — displayName is used for future extensibility
      void displayName;

      const mentionNode = $createMentionNode(mentionKey, mentionId);
      textNode.replace(mentionNode);
    },
    type: 'text-match',
  };
}
